import React, { useContext, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import ApplicationHealth from "./ApplicationHealth";
import ActuatorHealth from "./ActuatorHealth";
import KafkaHealth from "./KafkaHealth";
import ZookeeperHealth from "./ZookeeperHealth";
import { useDispatch, useSelector } from "react-redux";
import { getKafkaLag, getSystemHealth } from "../../redux/slices/health";
import NotyfContext from "../../contexts/NotyfContext";
import { useWhitelabel } from "../../contexts/WhitelabelContext";
import DatabaseHealth from "./DatabaseHealth";
import MailConfigurationsOverallHealth from "./MailConfigurationsOverallHealth";
import { getAllMailConfigurations } from "../../redux/slices/mailConfiguration";
import MailConfigurationsList from "./MailConfigurationsList";

import CpuHealth from "./CpuHealth";
import MemoryHealth from "./MemoryHealth";
import ThreadsHealth from "./ThreadsHealth";
import DiskHealth from "./DiskHealth";
import KafkaLag from "./KafkaLag";
import ActiveMQHealth from "./ActiveMQHealth";
import IngestionHealth from "./IngestionHealth";

const HealthDashboard = () => {
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const notyf = useContext(NotyfContext);
  const [activeUser, setActiveUser] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { whitelabelLoading, faviconUrl, whitelabelUrl } = useWhitelabel();
  const dispatch = useDispatch();
  const health = useSelector((state) => state.health);

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [impersonationUser, user]);

  useEffect(() => {
    const fetchHealthStatus = async () => {
      await Promise.all([
        dispatch(getSystemHealth()),
        dispatch(getAllMailConfigurations()),
        dispatch(getKafkaLag()),
      ]).catch((error) => {
        notyf.open({
          type: "error",
          message: error,
          duration: 5000,
          ripple: true,
          dismissible: false,
          position: {
            x: "center",
            y: "top",
          },
        });
      });
    };

    if (activeUser.id && activeUser.role === "admin") {
      fetchHealthStatus();
    }
  }, [activeUser]);

  return (
    <React.Fragment>
      <Helmet title="System Health" />
      <Container fluid className="p-0">
        <Row>
          <Col className="d-flex" sm={12} md={4} lg={2}>
            <ApplicationHealth health={health.applicationHealth} />
          </Col>
          <Col className="d-flex" sm={12} md={4} lg={2}>
            <ActuatorHealth health={health.actuatorHealth} />
          </Col>
          <Col className="d-flex" sm={12} md={4} lg={2}>
            <KafkaHealth health={health.kafkaHealth} />
          </Col>
          <Col className="d-flex" sm={12} md={4} lg={2}>
            <ZookeeperHealth health={health.zookeeperHealth} />
          </Col>
          <Col className="d-flex" sm={12} md={4} lg={2}>
            <DatabaseHealth health={health.databaseHealth} />
          </Col>
          <Col className="d-flex" sm={12} md={4} lg={2}>
            <MailConfigurationsOverallHealth
              health={health.mailServersHealth}
            />
          </Col>
        </Row>
        <Row>
          <Col className="d-flex">
            <ActiveMQHealth activemq={health.activeMqHealth} />
          </Col>
        </Row>
        <Row>
          <Col className="d-flex">
            <IngestionHealth ingestion={health.ingestionHealth} />
          </Col>
        </Row>
        <Row>
          <Col className="d-flex">
            <DiskHealth health={health.diskHealth} />
          </Col>
          <Col className="d-flex">
            <CpuHealth health={health.cpuHealth} />
          </Col>
          <Col className="d-flex">
            <MemoryHealth health={health.memoryHealth} />
          </Col>
        </Row>
        <Row>
          <Col className="d-flex">
            <KafkaLag
              allDrained={health.allDrained}
              totalLagAllGroups={health.totalLagAllGroups}
            />
          </Col>
          <Col className="d-flex">
            <ThreadsHealth health={health.threads} />
          </Col>
        </Row>
        <Row>
          <Col className="d-flex">
            <MailConfigurationsList />
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default HealthDashboard;
